import { useRef } from "react";

const useStrikethrough = () => {
  const savedRange = useRef<Range | null>(null);
  const savedFormState = useRef<{
    element: HTMLTextAreaElement | HTMLInputElement;
    start: number;
    end: number;
  } | null>(null);

  const saveSelection = () => {
    const activeEl = document.activeElement;
    if (
      activeEl &&
      (activeEl.tagName === "TEXTAREA" ||
        (activeEl.tagName === "INPUT" &&
          (activeEl as HTMLInputElement).type === "text"))
    ) {
      const el = activeEl as HTMLTextAreaElement | HTMLInputElement;
      savedFormState.current = {
        element: el,
        start: el.selectionStart || 0,
        end: el.selectionEnd || 0,
      };
      savedRange.current = null;
      return;
    }

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      savedRange.current = selection.getRangeAt(0).cloneRange();
      savedFormState.current = null;
    }
  };

  const getValidRange = () => {
    const selection = window.getSelection();
    let range: Range | null = null;
    if (selection && selection.rangeCount > 0) {
      range = selection.getRangeAt(0);
    } else if (savedRange.current) {
      range = savedRange.current;
    }
    return range;
  };

  const toggleStrikethroughForForm = (
    el: HTMLTextAreaElement | HTMLInputElement,
    start: number,
    end: number
  ) => {
    const val = el.value;
    const selectedText = val.slice(start, end);
    if (!selectedText) return;

    // Unicode combining long stroke overlay: U+0336
    const strikeChar = "\u0336";

    // Check if it's already struck through (every char followed by \u0336)
    // A simple regex check: does it look like c\u0336c\u0336...
    const isAlreadyStruck =
      selectedText.split("").every((char, i, arr) => {
        if (char === strikeChar) return true;
        if (arr[i + 1] === strikeChar) return true;
        return false;
      }) && selectedText.includes(strikeChar);

    let newText = "";
    if (isAlreadyStruck) {
      // Remove strikes
      newText = selectedText.split(strikeChar).join("");
    } else {
      // Add strikes
      newText = selectedText
        .split("")
        .map((char) => char + strikeChar)
        .join("");
    }

    el.value = val.slice(0, start) + newText + val.slice(end);
    el.focus();
    el.setSelectionRange(start, start + newText.length);

    const event = new Event("input", { bubbles: true });
    el.dispatchEvent(event);

    savedFormState.current = {
      element: el,
      start,
      end: start + newText.length,
    };
  };

  const applyStrikethrough = (range: Range) => {
    if (!range) return;

    // Check context
    const container = range.commonAncestorContainer;
    const allowedSelectors = [
      ".scratch-pad",
      ".word-processor",
      ".spreadsheet-view",
      "input",
      "textarea",
      '[contenteditable="true"]',
      ".page-content",
    ];

    let currentNode: Node | null = container;
    let isAllowed = false;

    while (currentNode && currentNode !== document.body) {
      if (currentNode.nodeType === 1) {
        const element = currentNode as Element;
        if (
          allowedSelectors.some(
            (selector) => element.matches && element.matches(selector)
          )
        ) {
          isAllowed = true;
          break;
        }
      }
      currentNode = currentNode.parentNode;
    }
    if (!isAllowed) return;

    const tempDiv = document.createElement("div");
    tempDiv.appendChild(range.cloneContents());

    tempDiv.querySelectorAll(".strikethrough").forEach((el) => {
      const parent = el.parentNode;
      if (!parent) return;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
    });

    const wrapper = document.createElement("span");
    wrapper.className = "strikethrough";
    wrapper.innerHTML = tempDiv.innerHTML;

    range.deleteContents();
    range.insertNode(wrapper);

    const mergeAdjacent = (span: any) => {
      const next = span.nextSibling;
      if (next && next.nodeType === 1 && next.className === span.className) {
        span.innerHTML += next.innerHTML;
        next.remove();
        mergeAdjacent(span);
      }
    };
    mergeAdjacent(wrapper);

    window.getSelection()?.removeAllRanges();
    savedRange.current = null;
  };

  const removeStrikethrough = (range: Range) => {
    if (!range) return;

    const container = range.commonAncestorContainer;
    let strikeNode: HTMLElement | null = null;
    let curr: Node | null = container;
    while (curr && curr !== document.body) {
      if (
        curr.nodeType === 1 &&
        (curr as Element).classList.contains("strikethrough")
      ) {
        strikeNode = curr as HTMLElement;
        break;
      }
      curr = curr.parentNode;
    }

    if (strikeNode) {
      const parent = strikeNode.parentNode;
      if (parent) {
        while (strikeNode.firstChild)
          parent.insertBefore(strikeNode.firstChild, strikeNode);
        parent.removeChild(strikeNode);
      }
    } else {
      // Fallback: strip internal strikes if any
      const tempDiv = document.createElement("div");
      tempDiv.appendChild(range.cloneContents());
      tempDiv.querySelectorAll(".strikethrough").forEach((el) => {
        const parent = el.parentNode;
        if (!parent) return;
        while (el.firstChild) parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
      });
      range.deleteContents();
      range.insertNode(tempDiv);
    }

    window.getSelection()?.removeAllRanges();
    savedRange.current = null;
  };

  const toggleStrikethrough = () => {
    const activeEl = document.activeElement;
    const isFormEl =
      activeEl &&
      (activeEl.tagName === "TEXTAREA" || activeEl.tagName === "INPUT");

    if (isFormEl || savedFormState.current) {
      const el = (isFormEl ? activeEl : savedFormState.current?.element) as
        | HTMLTextAreaElement
        | HTMLInputElement;
      const start = isFormEl
        ? el.selectionStart || 0
        : savedFormState.current!.start;
      const end = isFormEl ? el.selectionEnd || 0 : savedFormState.current!.end;
      toggleStrikethroughForForm(el, start, end);
      return;
    }

    const range = getValidRange();
    if (!range) return;

    let isStruck = false;
    let curr: Node | null = range.commonAncestorContainer;
    while (curr && curr !== document.body) {
      if (
        curr.nodeType === 1 &&
        (curr as Element).classList.contains("strikethrough")
      ) {
        isStruck = true;
        break;
      }
      curr = curr.parentNode;
    }

    if (isStruck) {
      removeStrikethrough(range);
    } else {
      applyStrikethrough(range);
    }
  };

  return {
    toggleStrikethrough,
    saveSelection,
  };
};

export default useStrikethrough;
